/**
 * OrgSwitcherDialog — modal picker for switching the active org.
 *
 * Opens from the sidebar's studio chip. Lists every org the user
 * belongs to (regular member OR client) with logo, name, role, and
 * a "Yours / Team" badge. Tapping a row triggers `setActiveOrg`
 * with a userDoc barrier so the rest of the app picks up the new
 * org snapshot before the dialog closes.
 *
 * After a successful switch we navigate to `/dashboard` — the
 * previously-open route may not exist or may not be accessible in
 * the new org (e.g. a deep project link that lives in the OLD org's
 * `projects` collection). Symmetric to first-sign-in landing
 * behaviour. The user can navigate back if their landing changes.
 *
 * Mirrors mobile's `OrgSwitcherSheet.tsx` so the two surfaces agree
 * on visual + behavioural details.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import { setActiveOrg } from '@/lib/setActiveOrg';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useMyOrganizations, type MyOrgRow } from '@/lib/useMyOrganizations';
import { useTokenClaims } from '@/lib/useTokenClaims';

export type OrgSwitcherDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function OrgSwitcherDialog({ open, onClose }: OrgSwitcherDialogProps) {
  const { user } = useAuth();
  const { org: currentOrg } = useFullOrganization();
  const { orgs: myOrgs, loading: orgsLoading } = useMyOrganizations();
  const { refresh: refreshClaims } = useTokenClaims();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [pendingTargetId, setPendingTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeOrgId = currentOrg?.id ?? null;
  const ownsAnOrg = myOrgs.some((o) => o.isYourStudio);

  const onPickOrg = useCallback(
    async (targetId: string) => {
      if (targetId === activeOrgId) {
        onClose();
        return;
      }
      if (!user) return;
      setPendingTargetId(targetId);
      setBusy(true);
      setError(null);
      try {
        // One-shot listener: resolves when the local users/{uid}
        // snapshot reflects the new primaryOrgId. ~150ms typical.
        // setActiveOrg awaits this so the dialog only closes after
        // the rest of the app has the new org context.
        const waitForUserDoc = () =>
          new Promise<void>((resolve) => {
            if (!user) return resolve();
            const unsub = onSnapshot(
              doc(db, 'users', user.uid),
              (snap) => {
                const next = snap.data() as
                  | { primaryOrgId?: string }
                  | undefined;
                if (next?.primaryOrgId === targetId) {
                  unsub();
                  resolve();
                }
              },
              () => {
                unsub();
                resolve();
              },
            );
          });

        await setActiveOrg(targetId, {
          refresh: refreshClaims,
          waitForUserDoc,
        });

        // Land on the dashboard. The previously-open route may not
        // exist in the new org or may be permission-denied for the
        // new role (e.g. CRM hidden for Supervisor).
        navigate('/dashboard');
        onClose();
      } catch (err) {
        console.warn('[OrgSwitcherDialog] switch failed:', err);
        setError(
          (err as { message?: string })?.message ||
            'Could not switch studio. Check your connection and try again.',
        );
      } finally {
        setBusy(false);
        setPendingTargetId(null);
      }
    },
    [activeOrgId, onClose, refreshClaims, user, navigate],
  );

  if (!open) return null;

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-switcher-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 201,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 480,
            maxHeight: 'calc(100vh - 32px)',
            background: 'var(--surface)',
            borderRadius: 14,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px 8px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <h2
                id="org-switcher-title"
                style={{ margin: 0, fontSize: 18, fontWeight: 700 }}
              >
                Switch studio
              </h2>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                }}
              >
                Choose where projects, transactions, and tasks load
                from. You stay signed in.
              </div>
            </div>
            <button
              type="button"
              onClick={() => !busy && onClose()}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 20,
                color: 'var(--text-secondary)',
                lineHeight: 1,
              }}
            >
              <i className="ph ph-x" />
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              padding: '8px 16px 12px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              flex: 1,
            }}
          >
            {orgsLoading && myOrgs.length === 0 ? (
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                Loading studios…
              </div>
            ) : myOrgs.length === 0 ? (
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                You're not a member of any studio yet.
              </div>
            ) : (
              myOrgs.map((row) => (
                <OrgRow
                  key={row.id}
                  row={row}
                  isActive={row.id === activeOrgId}
                  isPending={busy && pendingTargetId === row.id}
                  disabled={busy && pendingTargetId !== row.id}
                  onSelect={() => void onPickOrg(row.id)}
                />
              ))
            )}

            {/* "+ Create your studio" only when the user owns none */}
            {!ownsAnOrg && !orgsLoading ? (
              <button
                type="button"
                onClick={() => {
                  if (busy) return;
                  onClose();
                  // The onboarding flow lives at /onboarding/organization
                  // in the customer app; pass ?mode=add to bypass any
                  // "already onboarded" redirect.
                  navigate('/onboarding/organization?mode=add');
                }}
                disabled={busy}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  marginTop: 4,
                  border: '1.5px dashed var(--brand, #3b82f6)',
                  borderRadius: 14,
                  background: 'transparent',
                  cursor: busy ? 'wait' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'rgba(59,130,246,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--brand, #3b82f6)',
                    fontSize: 18,
                  }}
                >
                  <i className="ph ph-plus" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    Create your studio
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                    }}
                  >
                    Set up your own workspace · You stay signed in
                  </div>
                </div>
                <i
                  className="ph ph-arrow-right"
                  style={{ color: 'var(--brand, #3b82f6)' }}
                />
              </button>
            ) : null}

            {error ? (
              <div
                style={{
                  marginTop: 4,
                  padding: 10,
                  borderRadius: 8,
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

// ───── Row ────────────────────────────────────────────────────

function OrgRow({
  row,
  isActive,
  isPending,
  disabled,
  onSelect,
}: {
  row: MyOrgRow;
  isActive: boolean;
  isPending: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const accent = orgAccentColor(row.name);
  const initials = orgShortName(row.name);
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled || isPending}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderRadius: 14,
        border: isActive
          ? '1.5px solid var(--brand, #3b82f6)'
          : '1px solid var(--border)',
        background: isActive
          ? 'rgba(59,130,246,0.06)'
          : 'var(--surface)',
        cursor: disabled || isPending ? 'wait' : 'pointer',
        textAlign: 'left',
        width: '100%',
        opacity: disabled ? 0.6 : 1,
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: row.logoUrl ? 'transparent' : accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.4,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {row.logoUrl ? (
          <img
            src={row.logoUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initials
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 14,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.name}
          </span>
          <span
            style={{
              padding: '2px 7px',
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              background: row.isYourStudio
                ? 'rgba(16,185,129,0.12)'
                : 'rgba(100,116,139,0.10)',
              color: row.isYourStudio ? '#047857' : '#475569',
              flexShrink: 0,
            }}
          >
            {row.isYourStudio ? 'YOURS' : 'TEAM'}
          </span>
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          {row.roleLabel}
          {isActive ? ' · Active' : ' · Tap to switch'}
        </div>
      </div>
      <div style={{ width: 22, textAlign: 'right', flexShrink: 0 }}>
        {isPending ? (
          <span
            style={{
              display: 'inline-block',
              width: 14,
              height: 14,
              border: '2px solid rgba(59,130,246,0.25)',
              borderTopColor: 'var(--brand, #3b82f6)',
              borderRadius: '50%',
              animation: 'org-switch-spin 0.6s linear infinite',
            }}
          />
        ) : isActive ? (
          <i
            className="ph-fill ph-check-circle"
            style={{ color: 'var(--brand, #3b82f6)', fontSize: 20 }}
          />
        ) : (
          <i
            className="ph ph-caret-right"
            style={{ color: 'var(--text-tertiary, #94a3b8)' }}
          />
        )}
      </div>
      <style>{`
        @keyframes org-switch-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
}

// ───── Avatar helpers (lifted from mobile OrgSwitcherSheet) ────

const ACCENT_PALETTE = [
  '#0EA5E9',
  '#22C55E',
  '#F97316',
  '#A855F7',
  '#E11D48',
  '#0891B2',
  '#84CC16',
  '#EA580C',
  '#7C3AED',
  '#DB2777',
];

function orgAccentColor(name: string): string {
  if (!name) return ACCENT_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return ACCENT_PALETTE[hash % ACCENT_PALETTE.length];
}

function orgShortName(name: string): string {
  if (!name) return '·';
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w.charAt(0).toUpperCase()).join('');
}
